import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { ReviewFileUserStatus } from '@common/reviews/enums/review-file-user-status.enum';
import { ReviewFileUser } from '@common/reviews/models/review-file-user.model';
import { ReviewFile } from '@common/reviews/models/review-file.model';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { FlareTextEditorComponent } from '@portal-core/project-files/components/flare-text-editor/flare-text-editor.component';
import { ProjectsService } from '@portal-core/projects/services/projects.service';
import { ReviewFileUsersService } from '@portal-core/reviews/review-file-users/services/review-file-users.service';
import { EditorChangeEvent } from '@portal-core/text-editor/types/editor-change-event.type';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { Observable, of, switchMap } from 'rxjs';

@Component({
  selector: 'mc-flare-file-reviewer',
  templateUrl: './flare-file-reviewer.component.html',
  styleUrls: ['./flare-file-reviewer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlareFileReviewerComponent implements OnInit {
  @ViewChild('flareTextEditor', { static: true }) flareTextEditor: FlareTextEditorComponent;
  @Input() reviewPackageId: number;
  @Input() reviewer: LicenseUser;
  @Input() reviewFile: ReviewFile;
  @Input() reviewFileUser: ReviewFileUser;
  @Input() featureSetVersion: number;
  @Input() showConditions?: boolean;
  @Input() stylesEnabled?: boolean = true;

  @Output() createReview: EventEmitter<void> = new EventEmitter<void>();
  @Output() load: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() navToFile: EventEmitter<string> = new EventEmitter<string>();
  @Output() reviewerStatusUpdate: EventEmitter<ReviewFileUserStatus> = new EventEmitter<ReviewFileUserStatus>();
  @Output() showConditionsChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() stylesheetLoad: EventEmitter<void> = new EventEmitter<void>();
  @Output() stylesheetLoaded: EventEmitter<Error | undefined> = new EventEmitter<Error | undefined>();
  @Output() stylesEnabledChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @InputObservable('reviewFile') reviewFile$: Observable<ReviewFile>;

  CollabFileType: typeof CollabFileType = CollabFileType;

  projectLanguage$: Observable<string>;
  isRequestInProgress: boolean = false;

  constructor(
    private reviewFileUsersService: ReviewFileUsersService,
    private projectsService: ProjectsService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    // Create the observable for the language of the review file's project
    this.projectLanguage$ = this.reviewFile$.pipe(
      switchMap(reviewFile => {
        if (reviewFile) {
          return this.projectsService.getProjectLanguage$(reviewFile.ProjectId, reviewFile.CommitId);
        } else {
          return of(null);
        }
      }
      )
    );
  }

  onCreateNewReview() {
    this.createReview.emit();
  }

  onFileChanged(event: EditorChangeEvent, reviewFileId: number) {
    // A change event can be fired from collaborative changes or meta data loading. The user has only edited it if the event is dirty.
    if (!event.dirty || this.isRequestInProgress) {
      return;
    }

    // An owner isn't necessarily a reviewer so check if the review file user exists
    if (this.reviewFileUser && this.reviewFileUser.Status !== ReviewFileUserStatus.InProgress) {
      if (this.reviewFileUser.Status === ReviewFileUserStatus.Submitted) {
        this.snackBar.open(`Review file status changed from Submitted to In Progress.`, 'OK', { duration: SnackBarAutoDismissDurationMS });
      }

      this.isRequestInProgress = true; // Set the flag to indicate a request is in progress

      return this.reviewFileUsersService.updateMyReviewFileUserStatus$(reviewFileId, this.reviewFileUser.Id, ReviewFileUserStatus.InProgress).subscribe(() => {
        this.reviewerStatusUpdate.emit(ReviewFileUserStatus.InProgress);
        this.isRequestInProgress = false;
      }, () => {
        this.isRequestInProgress = false;
      });
    }
  }

  onShowConditionsChanged(showConditions: boolean) {
    this.showConditions = showConditions;
    this.showConditionsChange.emit(showConditions);
  }

  onStylesEnabledChanged(stylesEnabled: boolean) {
    this.stylesEnabled = stylesEnabled;
    this.stylesEnabledChange.emit(stylesEnabled);
  }

  onSubEditorChanged(status: ReviewFileUserStatus) {
    this.reviewerStatusUpdate.emit(status);
  }

  public reload() {
    this.flareTextEditor?.reload();
  }
}
